"use client"

import { useState, useEffect, useRef } from "react"
import { getDatabase, ref, onValue, remove, get, update } from "firebase/database"
import { onAuthStateChanged } from "firebase/auth"
import { auth } from "../Firebase/firestore"
import { FaTrash, FaChevronDown } from "react-icons/fa"
import "./notifications.css"

const SupplierNotifications = ({ adminView = false }) => {
  const [notifications, setNotifications] = useState([])
  const [filteredNotifications, setFilteredNotifications] = useState([])
  const [error, setError] = useState("")
  const [userId, setUserId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState("all")

  const brand = adminView ? localStorage.getItem("adminSelectedBrand") : localStorage.getItem("supplierBrand")

  // Use refs to track active listeners
  const listeners = useRef([])
  const allNotificationsRef = useRef([])

  // Track if component is mounted
  const isMounted = useRef(true)

  // Notification categories for dropdown
  const notificationCategories = [
    { value: "all", label: "All Notifications" },
    { value: "New Orders", label: "New Orders" },
    { value: "Steel Orders", label: "Steel Orders" },
    { value: "New Query", label: "New Query" },
    { value: "Negotiations", label: "Negotiations" },
    { value: "Quotations", label: "Quotations" },
    { value: "Raised Issues", label: "Raised Issues" },
    { value: "Machinery Orders", label: "Machinery Orders" },
    { value: "transportGoods", label: "Transport Goods" },
    { value: "Stop_Order", label: "Stop Order" },
    { value: "Refund", label: "Refund" },
    { value: "finance", label: "Finance" },
    { value: "Missing_Docs", label: "Missing Docs" },
    { value: "StockStatus", label: "Stock Status" },
    { value: "Document Verification", label: "Document Verification" },
    { value: "Docs_Request", label: "Docs Request" },
    { value: "firm_requests", label: "Firm Request" },
    { value: "Transport_Goods", label: "Transport Goods" },
  ]

  // Cleanup function to detach all listeners
  const cleanupListeners = () => {
    listeners.current.forEach((unsubscribe) => unsubscribe())
    listeners.current = []
  }

  // Filter notifications based on selected category
  useEffect(() => {
    if (selectedCategory === "all") {
      setFilteredNotifications(notifications)
    } else {
      const filtered = notifications.filter((notif) => notif.type === selectedCategory)
      setFilteredNotifications(filtered)
    }
  }, [notifications, selectedCategory])

  useEffect(() => {
    isMounted.current = true
    if (adminView) {
      fetchAllNotifications()
    } else {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user && isMounted.current) {
          setUserId(user.uid)
          fetchAllNotifications()
        } else if (isMounted.current) {
          setError("User not authenticated.")
          setIsLoading(false)
        }
      })

      return () => {
        unsubscribe()
        cleanupListeners()
        isMounted.current = false
      }
    }

    return () => {
      cleanupListeners()
      isMounted.current = false
    }
  }, [brand, adminView])

  const fetchAllNotifications = () => {
    cleanupListeners()
    const db = getDatabase()
    const types = [
      "Steel Orders",
      "New Query",
      "Negotiations",
      "Quotations",
      "Raised Issues",
      "Machinery Orders",
      "transportGoods",
      "Stop_Order",
      "Refund",
      "finance",
      "New Orders",
      "Missing_Docs",
      "StockStatus",
      "PaymentProofAdded",
      "Document Verification",
      "Docs_Request",
      "firm_requests",
      "Transport_Goods"
    ]

    setNotifications([])
    allNotificationsRef.current = []

    let listenersCompleted = 0

    types.forEach((type) => {
      const typeRef = ref(db, `supplier_notifications/${type}`)
      const unsubscribe = onValue(
        typeRef,
        (snapshot) => {
          if (snapshot.exists() && isMounted.current) {
            Object.entries(snapshot.val()).forEach(
              ([notificationUserId, userNotifications]) => {
                if (
                  userNotifications &&
                  typeof userNotifications === "object"
                ) {
                  Object.entries(userNotifications).forEach(
                    ([id, notification]) => {
                      if (
                        notification &&
                        typeof notification === "object" &&
                        // Admin view: include all except "ActivePO"
                        ((adminView &&
                          notification.type?.toLowerCase() !== "activepo") ||
                          // Supplier view: include only notifications for their brand
                          (!adminView &&
                            notification.brand?.trim().toLowerCase() ===
                              brand?.trim().toLowerCase()))
                      ) {
                        let timestamp = notification.timestamp;
                        if (
                          !timestamp ||
                          isNaN(new Date(timestamp).getTime())
                        ) {
                          timestamp = new Date().toISOString();
                        }

                        const now = Date.now();
                        const notificationTime = new Date(timestamp).getTime();
                        const isNew =
                          now - notificationTime < 48 * 60 * 60 * 1000;
                        const notificationWithMeta = {
                          id,
                          userId: notificationUserId,
                          type,
                          isNew,
                          ...notification,
                          timestamp,
                          showGif:
                            isNew && notification.gifUrl && !notification.seen,
                          unseen: !notification.seen, // <<< THIS IS THE LINE YOU ASKED ABOUT
                        };

                        allNotificationsRef.current.push(notificationWithMeta);
                      }
                    }
                  );
                }
              }
            );
          }

          listenersCompleted++;
          if (listenersCompleted === types.length && isMounted.current) {
            allNotificationsRef.current.sort((a, b) => {
              return new Date(b.timestamp) - new Date(a.timestamp);
            });

            setNotifications([...allNotificationsRef.current]);
            setIsLoading(false);
            setError(
              allNotificationsRef.current.length
                ? ""
                : "No notifications to display."
            );
          }
        },
        (error) => {
          console.error(`Error fetching ${type} notifications:`, error)
          if (isMounted.current) {
            setError("Error fetching notifications.")
            setIsLoading(false)
          }
          listenersCompleted++
        },
      )

      listeners.current.push(unsubscribe)
    })
  }

  const handleNotificationClick = (notification) => {
    const db = getDatabase()
    const notificationRef = ref(
      db,
      `supplier_notifications/${notification.type}/${notification.userId}/${notification.id}`,
    )

    update(notificationRef, {
      seen: true,
      showGif: false,
    })

    setNotifications((prev) =>
      prev.map((item) =>
        item.id === notification.id && item.type === notification.type && item.userId === notification.userId
          ? { ...item, seen: true, showGif: false }
          : item,
      ),
    )

    if (!notification.seen && window.decreaseUnreadCount) {
      window.decreaseUnreadCount()
    }
    
const viewMap = {
  "New Orders": "userOrders",
  "Steel Orders": "userSteelOrders",
  "New Query": "userQueries",
  "Negotiations": "userQuotations",
  "Quotations": "userQuotations",
  "Raised Issues": "raisedIssue",
  "Machinery Orders": "userMachineryOrders",
  "transportGoods": "transport-goods-reply",
  "Stop_Order": "userOrders",
  "Refund": "refunds",
  "finance": "finance",
  "Missing_Docs": "clientRecords",
  "Docs_Request": "clientRecords",
  "Document Verification": "clientRecords",
  "StockStatus": "stockStatus",
  "PaymentProofAdded": "userOrders",
  "Payment Confirmation": "userOrders",
  "firm_requests": "AdminFirmRequests",
  "Transport Goods": "TransportGoodsReplies",
}

    if (viewMap[notification.type]) {
      const event = new CustomEvent("changeView", {
        detail: {
          view: viewMap[notification.type],
          notificationId: notification.id,
          orderType:
            notification.title === "Order Paused"
              ? "paused"
              : notification.title === "Order Continued"
                ? "continued"
                : null,
        },
      })
      window.dispatchEvent(event)
    }
  }

  const handleDeleteNotification = async (notification) => {
    try {
      const db = getDatabase()
      const notificationRef = ref(
        db,
        `supplier_notifications/${notification.type}/${notification.userId}/${notification.id}`,
      )

      await remove(notificationRef)

      setNotifications((prev) =>
        prev.filter(
          (item) =>
            !(item.id === notification.id && item.type === notification.type && item.userId === notification.userId),
        ),
      )

      allNotificationsRef.current = allNotificationsRef.current.filter(
        (item) =>
          !(item.id === notification.id && item.type === notification.type && item.userId === notification.userId),
      )

      if (notifications.length <= 1) {
        setError("No notifications to display.")
      }
    } catch (err) {
      console.error("Error deleting notification:", err)
    }
  }

  // Fixed removeOldNotifications function
  const removeOldNotifications = async () => {
    const db = getDatabase()
    const types = [
      "Steel Orders",
      "New Query",
      "Negotiations",
      "Quotations",
      "Raised Issues",
      "Machinery Orders",
      "transportGoods",
      "Stop_Order",
      "Refund",
      "finance",
      "New Orders",
      "Missing_Docs",
      "StockStatus",
      "Payment Confirmation",
      "PaymentProofAdded",
      "Document Verification",
      "Docs_Request",
      "firm_requests",
      "Transport_Goods",
    ]

    const now = Date.now()
    const threshold = 48 * 60 * 60 * 1000 

    for (const type of types) {
      try {
        const typeRef = ref(db, `supplier_notifications/${type}`)
        const snapshot = await get(typeRef)

        if (snapshot.exists()) {
          const typeData = snapshot.val()

          // Process each user's notifications
          for (const [userId, userNotifications] of Object.entries(typeData)) {
            if (userNotifications && typeof userNotifications === "object") {
              const updates = {}

              // Check each notification for this user
              for (const [notificationId, notification] of Object.entries(userNotifications)) {
                if (notification && typeof notification === "object") {
                  const timestamp = notification.timestamp

                  // Skip if timestamp is invalid
                  if (!timestamp || isNaN(new Date(timestamp).getTime())) {
                    continue
                  }

                  const notificationTime = new Date(timestamp).getTime()

                  // If notification is older than 48 hours, mark for deletion
                  if (now - notificationTime > threshold) {
                    updates[`${userId}/${notificationId}`] = null
                    console.log(
                      `Marking for deletion: ${type}/${userId}/${notificationId} - Age: ${Math.round((now - notificationTime) / (1000 * 60 * 60))} hours`,
                    )
                  }
                }
              }

              // Apply deletions if any
              if (Object.keys(updates).length > 0) {
                const updateRef = ref(db, `supplier_notifications/${type}`)
                await update(updateRef, updates)
                console.log(`Deleted ${Object.keys(updates).length} old notifications from ${type}`)
              }
            }
          }
        }
      } catch (error) {
        console.error(`Error cleaning up ${type} notifications:`, error)
      }
    }
  }

  // Run cleanup immediately on mount and then every 6 hours
  useEffect(() => {
    // Run cleanup immediately
    removeOldNotifications()

    // Set up interval for periodic cleanup
    const interval = setInterval(
      () => {
        removeOldNotifications()
      },
      6 * 60 * 60 * 1000, // every 6 hours
    )

    return () => clearInterval(interval)
  }, [])

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value)
  }

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp)
      if (isNaN(date.getTime())) return timestamp

      const now = new Date()
      const diffInMinutes = Math.floor((now - date) / (1000 * 60))

      if (diffInMinutes < 1) return "Just now"
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`

      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch (error) {
      return timestamp
    }
  }

  return (
    <div className="notify-btn">
      <h4 className="text-center">
        Notifications
        {notifications.filter(n => !n.seen).length > 0 && (
    <span className="total-badge">{notifications.filter(n => !n.seen).length}</span>
)}
      </h4>

      <div className="category-filter">
        <div className="dropdown-wrapper">
          <select value={selectedCategory} onChange={handleCategoryChange} className="category-dropdown">
            {notificationCategories.map((category) => {
              const categoryCount =
    category.value === "all"
        ? notifications.filter(n => !n.seen).length
        : notifications.filter((n) => n.type === category.value && !n.seen).length

              return (
                <option key={category.value} value={category.value}>
                  {category.label} {categoryCount > 0 && `(${categoryCount})`}
                </option>
              )
            })}
          </select>
          <FaChevronDown className="dropdown-icon" />
        </div>
      </div>

      {isLoading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading notifications...</p>
        </div>
      ) : error ? (
        <p className="error-message">{error}</p>
      ) : (
        <ul className="notifications-list">
          {filteredNotifications.map((notif) => (
            <li
              key={`${notif.type}-${notif.userId}-${notif.id}`}
              className={`notification ${notif.isNew ? "flash" : ""} ${!notif.seen ? "unseen" : ""}`}
              onClick={() => handleNotificationClick(notif)}
            >
              <div className="notification-header">
                <div className="notification-title-wrapper">
                  {!notif.seen && <div className="unseen-indicator"></div>}
                  <div className="notification-title">
                    {notif.showGif && !notif.seen && (
                      <img src={notif.gifUrl || "/placeholder.svg"} alt="GIF" className="notification-gif" />
                    )}
                    <strong className="text-success">{notif.title || notif.type}</strong>
                    <span className="notification-type-tag">{notif.type}</span>
                    {notif.brand && <span className="notification-brand-tag">{notif.brand}</span>}
                  </div>
                </div>
                <div className="notification-actions">
                  <button
                    className="delete-button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteNotification(notif)
                    }}
                    title="Delete notification"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
              <div className="notification-content">
                <div className="text-message">
                  <span>
                    <strong>{notif.message}</strong>
                  </span>
                  <small>{formatTimestamp(notif.timestamp)}</small>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {filteredNotifications.length === 0 && !isLoading && !error && (
        <div className="no-notifications">
          <div className="empty-icon">🔔</div>
          <p>No notifications found for the selected category.</p>
        </div>
      )}
    </div>
  )
}

export default SupplierNotifications